/**
 * AssignRoleModal — unified role assignment entry point.
 *
 * Three-tab selector for assigning access to a user:
 *   Package  (indigo) — apply a persona or functional role package
 *   Access   (purple) — workspace-member baseline + grant workspace admin
 *   Role     (blue)   — assign one or more job roles (multi-select)
 *
 * The modal manages its own API calls internally and calls `onSuccess` (which
 * should trigger a data refresh) then leaves the modal open for further assigns.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import {
  X,
  Search,
  Check,
  Loader2,
  Package,
  Shield,
  ChevronDown,
  ChevronUp,
  Star,
  KeyRound,
} from 'lucide-react';
import {
  ROLE_PACKAGES,
  ROLE_PACKAGE_THEME_LABELS,
  getRoleSlugTier,
  type RolePackageDefinition,
} from '../../types/prioritization/rolePackages';
import { DEFAULT_JOB_ROLES, JOB_FAMILIES, type JobFamilyId } from '../../types/prioritization/jobRoles';
import { ACTOR_ROLE_PERMISSIONS } from '../../types/prioritization/actorRolePermissions';
import { getSupabaseClient, isSupabaseConfigured } from '../../lib/supabaseClient';
import { resolveGrantScope } from '../../lib/roleScope';
import { createRoleAssignment } from '../../lib/rolesService';
import { createRoleAssignmentRpc, elevateAdmin } from '../../lib/delegationRpc';
import type { OrgUnit } from '../../types/prioritization/enterpriseIdentity';
import { hasTenantAdminRole, hasPlatformAdminRole } from '../../lib/authorization/enforce';
import { useUserStore } from '../../stores/prioritization/userStore';

// ============================================================================
// Types
// ============================================================================

type AssignMode = 'package' | 'access' | 'role';

interface ModalUser {
  id: string;
  name: string;
  email: string;
}

interface DbRole {
  id: string;
  name: string;
  displayName: string;
}

export interface AssignRoleModalProps {
  user: ModalUser;
  workspaceId: string | null;
  currentUserId: string | null;
  /** Role IDs the user already holds directly (preselected / marked held). */
  heldRoleIds?: string[];
  /** True when the user already has workspace-admin for this workspace. */
  isWorkspaceAdmin?: boolean;
  orgUnits: OrgUnit[];
  onSuccess: () => void;
  onClose: () => void;
}

// ============================================================================
// Tab config
// ============================================================================

const TABS: { id: AssignMode; label: string; icon: React.ReactNode; activeClass: string; hoverClass: string }[] = [
  {
    id: 'package',
    label: 'Package',
    icon: <Package className="size-3.5" />,
    activeClass: 'bg-[var(--slate)] text-white',
    hoverClass: 'hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 text-[var(--text-ter)] dark:text-[var(--text-ter)]',
  },
  {
    id: 'access',
    label: 'Access',
    icon: <KeyRound className="size-3.5" />,
    activeClass: 'bg-[var(--slate)] text-white',
    hoverClass: 'hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 text-[var(--text-ter)] dark:text-[var(--text-ter)]',
  },
  {
    id: 'role',
    label: 'Individual Role',
    icon: <Shield className="size-3.5" />,
    activeClass: 'bg-[var(--slate)] text-white',
    hoverClass: 'hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 text-[var(--text-ter)] dark:text-[var(--text-ter)]',
  },
];

const MEMBER_BASELINE = ACTOR_ROLE_PERMISSIONS['workspace-member'];

// ============================================================================
// Package tab
// ============================================================================

function PackageTab({
  user,
  workspaceId,
  currentUserId,
  onSuccess,
}: {
  user: ModalUser;
  workspaceId: string | null;
  currentUserId: string | null;
  onSuccess: () => void;
}) {
  const [query, setQuery] = useState('');
  const [selectedPkg, setSelectedPkg] = useState<RolePackageDefinition | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setTimeout(() => searchRef.current?.focus(), 50); }, []);

  const themes = useMemo(() => {
    const lq = query.toLowerCase().trim();
    const filtered = ROLE_PACKAGES.filter(
      (p) =>
        !lq ||
        p.displayName.toLowerCase().includes(lq) ||
        p.typicalPersona.toLowerCase().includes(lq) ||
        p.description.toLowerCase().includes(lq),
    );

    const personas = filtered.filter((p) => p.theme === 'cross-functional');
    const functional = filtered.filter((p) => p.theme !== 'cross-functional');

    const themeGroups = new Map<string, RolePackageDefinition[]>();
    for (const pkg of functional) {
      const list = themeGroups.get(pkg.theme) ?? [];
      list.push(pkg);
      themeGroups.set(pkg.theme, list);
    }

    return { personas, themeGroups };
  }, [query]);

  function toggleExpand(slug: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }

  async function handleAssign() {
    if (!selectedPkg || !workspaceId || !currentUserId) return;
    setAssigning(true);
    setError('');
    const supabase = getSupabaseClient();
    const errors: string[] = [];

    for (const slug of selectedPkg.roleSlug) {
      const { data: roleRow } = await supabase
        .from('roles').select('id').eq('name', slug).maybeSingle();
      if (!roleRow?.id) { errors.push(`Role not found: ${slug}`); continue; }
      const tier = getRoleSlugTier(selectedPkg, slug);
      try {
        await createRoleAssignment(
          { subjectType: 'user', subjectId: user.id, roleId: roleRow.id, scopeKind: 'global', scopeId: workspaceId, tier },
          currentUserId,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.toLowerCase().includes('duplicate') && !msg.toLowerCase().includes('unique')) {
          errors.push(`${slug}: ${msg}`);
        }
      }
    }

    setAssigning(false);
    if (errors.length === 0) {
      setDone(true);
      onSuccess();
    } else {
      setError(errors.join('\n'));
    }
  }

  function renderPackageCard(pkg: RolePackageDefinition) {
    const isSelected = selectedPkg?.slug === pkg.slug;
    const isOpen = expanded.has(pkg.slug);
    const primaryCount = pkg.primaryRoleSlug?.length ?? 0;
    const supportingCount = pkg.roleSlug.length - primaryCount;

    return (
      <div
        key={pkg.slug}
        className={` border transition-colors cursor-pointer ${
          isSelected
            ? 'border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20'
            : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:border-[var(--slate)] dark:hover:border-[var(--slate)]'
        }`}
      >
        <div
          className="flex items-start gap-3 px-3 py-2.5"
          onClick={() => setSelectedPkg(isSelected ? null : pkg)}
        >
          <div className={`mt-0.5 size-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
            isSelected ? 'border-[var(--slate)] bg-[var(--slate)]' : 'border-[var(--rule)]'
          }`}>
            {isSelected && <div className="size-1.5 rounded-full bg-[var(--bg-raised)]" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                {pkg.displayName}
              </span>
              {pkg.theme === 'cross-functional' && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 px-1.5 py-0.5 text-[10px] font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
                  <Star className="size-2.5" />Persona
                </span>
              )}
            </div>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5 truncate">
              {pkg.typicalPersona}
            </p>
            {primaryCount > 0 && (
              <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                {primaryCount} primary · {supportingCount} supporting
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleExpand(pkg.slug); }}
            className="shrink-0 p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
          >
            {isOpen ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
          </button>
        </div>
        {isOpen && (
          <div className="px-3 pb-3 border-t border-[var(--rule)] dark:border-[var(--rule)] mt-0 pt-2">
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-2">{pkg.description}</p>
            {pkg.primaryRoleSlug && pkg.primaryRoleSlug.length > 0 && (
              <div className="mb-1.5">
                <p className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-1">Primary</p>
                <div className="flex flex-wrap gap-1">
                  {pkg.primaryRoleSlug.map((s) => (
                    <span key={s} className="rounded bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-sec)]">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide mb-1">Supporting</p>
              <div className="flex flex-wrap gap-1">
                {pkg.roleSlug
                  .filter((s) => !pkg.primaryRoleSlug?.includes(s))
                  .map((s) => (
                    <span key={s} className="rounded bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-1.5 py-0.5 text-[10px] text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      {s}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (done) {
    return (
      <div className="bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)] p-4 flex gap-3 items-start">
        <Check className="size-5 text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
            "{selectedPkg?.displayName}" applied to {user.name}
          </p>
          <p className="mt-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
            {selectedPkg?.roleSlug.length} role{(selectedPkg?.roleSlug.length ?? 0) !== 1 ? 's' : ''} assigned.
            {selectedPkg?.primaryRoleSlug && ` ${selectedPkg.primaryRoleSlug.length} marked primary.`}
          </p>
        </div>
      </div>
    );
  }

  const hasResults = themes.personas.length > 0 || themes.themeGroups.size > 0;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[var(--text-ter)]" />
        <input
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search packages…"
          className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] pl-8 pr-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div className="max-h-72 overflow-y-auto space-y-3 pr-0.5">
        {!hasResults && (
          <p className="py-6 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-dim)]">No packages match your search.</p>
        )}

        {themes.personas.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide mb-1.5">Persona packages</p>
            <div className="space-y-1.5">
              {themes.personas.map(renderPackageCard)}
            </div>
          </div>
        )}

        {Array.from(themes.themeGroups.entries()).map(([theme, pkgs]) => (
          <div key={theme}>
            <p className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide mb-1.5">
              {ROLE_PACKAGE_THEME_LABELS[theme as keyof typeof ROLE_PACKAGE_THEME_LABELS] ?? theme}
            </p>
            <div className="space-y-1.5">
              {pkgs.map(renderPackageCard)}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] p-3">
          <p className="text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)] mb-1">Some roles could not be assigned:</p>
          <pre className="text-xs text-[var(--coral)] dark:text-[var(--coral)] whitespace-pre-wrap">{error}</pre>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          disabled={!selectedPkg || assigning}
          onClick={() => void handleAssign()}
          className="bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {assigning && <Loader2 className="size-3.5 animate-spin" />}
          Apply package
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Access tab — workspace-member baseline + workspace admin
// ============================================================================

function AccessTab({
  user,
  workspaceId,
  isWorkspaceAdmin: isAdminProp,
  onSuccess,
}: {
  user: ModalUser;
  workspaceId: string | null;
  isWorkspaceAdmin?: boolean;
  onSuccess: () => void;
}) {
  const [isAdmin, setIsAdmin] = useState(Boolean(isAdminProp));
  const [checking, setChecking] = useState(!isAdminProp);

  /**
   * Only a tenant admin may grant workspace admin (delegation policy, mig 197).
   * The button used to be offered to every workspace admin and refused after the
   * click — an action shown to someone who can never take it, failing late and
   * naming a UUID. Ask the same question the server will ask, before offering it.
   */
  const assignedRoleNames = useUserStore((st) => st.assignedRoleNames);
  const viewerMayGrantAdmin =
    hasTenantAdminRole(assignedRoleNames ?? []) || hasPlatformAdminRole(assignedRoleNames ?? []);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setIsAdmin(Boolean(isAdminProp));
  }, [isAdminProp, user.id]);

  useEffect(() => {
    if (isAdminProp || !workspaceId || !isSupabaseConfigured()) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setChecking(true);
      try {
        const supabase = getSupabaseClient();
        const { data: role } = await supabase
          .from('roles')
          .select('id')
          .eq('name', 'workspace-admin')
          .maybeSingle();
        let held = false;
        if (role?.id) {
          const { data: assignment } = await supabase
            .from('role_assignments')
            .select('id')
            .eq('subject_type', 'user')
            .eq('subject_id', user.id)
            .eq('role_id', role.id)
            .eq('scope_id', workspaceId)
            .eq('active', true)
            .maybeSingle();
          held = Boolean(assignment?.id);
        }
        if (!held) {
          const { data: membership } = await supabase
            .from('workspace_memberships')
            .select('role')
            .eq('user_id', user.id)
            .eq('workspace_id', workspaceId)
            .maybeSingle();
          const memRole = (membership?.role ?? '').toLowerCase();
          held = memRole === 'admin' || memRole === 'workspace-admin' || memRole === 'owner';
        }
        if (!cancelled) setIsAdmin(held);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id, workspaceId, isAdminProp]);

  async function handleGrantAdmin() {
    if (!workspaceId || isAdmin) return;
    setSaving(true);
    setError('');
    try {
      const result = await elevateAdmin({
        targetUserId: user.id,
        adminRole: 'WORKSPACE_ADMIN',
        scopeId: workspaceId,
      });
      if (!result.success) throw new Error(result.error ?? 'Failed to grant workspace admin');
      setIsAdmin(true);
      setDone(true);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant workspace admin');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Baseline access every workspace member holds, plus elevation to workspace admin.
      </p>

      {/* Workspace member baseline */}
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 size-4 rounded border-2 border-[var(--green)] bg-[var(--green)] flex items-center justify-center shrink-0">
            <Check className="size-2.5 text-white" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Workspace member
              </p>
              <span className="rounded-full bg-[var(--green-dim)] dark:bg-[var(--green)]/30 px-1.5 py-0.5 text-[10px] font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                Baseline — always held
              </span>
            </div>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">
              Minimal access granted to every workspace member. Job roles and packages add authority on top.
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {MEMBER_BASELINE.map((perm) => (
                <span
                  key={perm}
                  className="rounded bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-1.5 py-0.5 text-sm font-mono text-[var(--text-sec)] dark:text-[var(--text-sec)]"
                >
                  {perm}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Workspace admin */}
      <div
        className={` border p-3 ${
          isAdmin
            ? 'border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]'
            : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]'
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 size-4 rounded border-2 shrink-0 flex items-center justify-center ${
              isAdmin
                ? 'border-[var(--slate)] bg-[var(--slate)]'
                : 'border-[var(--rule)]'
            }`}
          >
            {isAdmin && <Check className="size-2.5 text-white" strokeWidth={3} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Workspace admin
              </p>
              {checking ? (
                <span className="text-[10px] text-[var(--text-ter)]">Checking…</span>
              ) : isAdmin ? (
                <span className="rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-sec)]">
                  Already held
                </span>
              ) : null}
            </div>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">
              Full workspace settings, users, and process authority for this workspace (cannot grant admin to others).
            </p>
          </div>
        </div>
      </div>

      {done && (
        <div className="bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)] p-3 flex gap-2 items-start">
          <Check className="size-4 text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5 shrink-0" />
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            {user.name} is now a workspace admin.
          </p>
        </div>
      )}

      {!viewerMayGrantAdmin && !isAdmin && (
        <p className="text-xs text-[var(--text-ter)]">
          A workspace admin cannot make someone else a workspace admin. A tenant admin can.
        </p>
      )}

      {error && (
        <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          disabled={!workspaceId || isAdmin || saving || checking || !viewerMayGrantAdmin}
          onClick={() => void handleGrantAdmin()}
          title={
            !viewerMayGrantAdmin && !isAdmin
              ? 'A workspace admin cannot make someone else a workspace admin. A tenant admin can.'
              : undefined
          }
          className="bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving && <Loader2 className="size-3.5 animate-spin" />}
          {isAdmin ? 'Already workspace admin' : 'Make workspace admin'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Individual Role tab — multi-select with held preselect
// ============================================================================

function RoleTab({
  user,
  workspaceId,
  orgUnits,
  heldRoleIds,
  onSuccess,
}: {
  user: ModalUser;
  workspaceId: string | null;
  orgUnits: OrgUnit[];
  heldRoleIds: string[];
  onSuccess: () => void;
}) {
  const heldSet = useMemo(() => new Set(heldRoleIds), [heldRoleIds]);
  const [dbRoles, setDbRoles] = useState<DbRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(() => [...heldRoleIds]);
  const [orgUnitId, setOrgUnitId] = useState('*');
  const [saving, setSaving] = useState(false);
  const [doneCount, setDoneCount] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void loadRoles();
  }, []);

  useEffect(() => {
    setSelectedRoleIds([...heldRoleIds]);
    setDoneCount(null);
    setError('');
  }, [user.id, heldRoleIds]);

  async function loadRoles() {
    if (!isSupabaseConfigured()) { setLoading(false); return; }
    const supabase = getSupabaseClient();
    const { data } = await supabase
      .from('roles')
      .select('id, name, display_name')
      .eq('role_type', 'job')
      .order('display_name');
    setDbRoles(
      (data ?? []).map((r: { id: string; name: string; display_name: string }) => ({
        id: r.id,
        name: r.name,
        displayName: r.display_name,
      })),
    );
    setLoading(false);
  }

  const familyGroups = useMemo(() => {
    const lq = query.toLowerCase().trim();
    const matched = DEFAULT_JOB_ROLES.filter(
      (r) => !lq || r.displayName.toLowerCase().includes(lq) || r.description.toLowerCase().includes(lq),
    );
    const map = new Map<JobFamilyId, typeof matched>();
    for (const r of matched) {
      const list = map.get(r.family) ?? [];
      list.push(r);
      map.set(r.family, list);
    }
    return map;
  }, [query]);

  const newlySelectedIds = selectedRoleIds.filter((id) => !heldSet.has(id));

  function toggleRole(roleId: string) {
    if (!roleId || heldSet.has(roleId)) return;
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
  }

  async function handleAssign() {
    if (newlySelectedIds.length === 0 || !workspaceId) return;
    setSaving(true);
    setError('');
    const errors: string[] = [];
    let assigned = 0;

    // One shared rule for every grant writer (lib/roleScope.ts, mirrored by
    // org_unit_scope_kind in migration 722): workspace-wide → resource; org
    // unit → depth-based kind.
    const selectedUnit = orgUnitId === '*' ? null : orgUnits.find((o) => o.id === orgUnitId);
    const { scopeKind, scopeId } = resolveGrantScope({
      orgUnit: selectedUnit ? { id: selectedUnit.id, depth: selectedUnit.depth } : orgUnitId !== '*' ? { id: orgUnitId } : null,
      workspaceId,
    });

    for (const roleId of newlySelectedIds) {
      const result = await createRoleAssignmentRpc({
        subjectType: 'user',
        subjectId: user.id,
        roleId,
        scopeKind,
        scopeId,
        tier: 'supporting',
      });
      if (!result.success) {
        const msg = result.error ?? 'Failed';
        if (!msg.toLowerCase().includes('duplicate') && !msg.toLowerCase().includes('unique')) {
          const label = dbRoles.find((r) => r.id === roleId)?.displayName ?? roleId;
          errors.push(`${label}: ${msg}`);
        } else {
          assigned += 1;
        }
      } else {
        assigned += 1;
      }
    }

    setSaving(false);
    if (errors.length === 0) {
      setDoneCount(assigned);
      onSuccess();
    } else {
      setError(errors.join('\n'));
      if (assigned > 0) onSuccess();
    }
  }

  if (doneCount !== null) {
    return (
      <div className="bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)] p-4 flex gap-3 items-start">
        <Check className="size-5 text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
            {doneCount} role{doneCount !== 1 ? 's' : ''} assigned to {user.name}
          </p>
          <p className="mt-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
            Assigned as supporting roles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[var(--text-ter)]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search roles…"
          className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] pl-8 pr-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Select one or more roles. Roles already held stay checked.
        {newlySelectedIds.length > 0 && (
          <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] font-medium">
            {' '}{newlySelectedIds.length} new to assign.
          </span>
        )}
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-[var(--text-ter)]">
          <Loader2 className="size-4 animate-spin mr-2" />
          <span className="text-sm">Loading roles…</span>
        </div>
      ) : (
        <div className="max-h-60 overflow-y-auto space-y-3 pr-0.5">
          {familyGroups.size === 0 ? (
            <p className="py-6 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-dim)]">No roles match your search.</p>
          ) : (
            Array.from(familyGroups.entries()).map(([family, roles]) => {
              const familyLabel = JOB_FAMILIES[family]?.label ?? family;
              return (
                <div key={family}>
                  <p className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide mb-1">
                    {familyLabel}
                  </p>
                  <div className="space-y-0.5">
                    {roles.map((role) => {
                      const dbRole = dbRoles.find((r) => r.name === role.slug);
                      const rid = dbRole?.id ?? '';
                      const isHeld = rid !== '' && heldSet.has(rid);
                      const isSelected = rid !== '' && selectedRoleIds.includes(rid);
                      return (
                        <button
                          key={role.slug}
                          type="button"
                          disabled={!rid || isHeld}
                          onClick={() => toggleRole(rid)}
                          className={`w-full flex items-center gap-3  px-3 py-2 text-left transition-colors ${
                            isSelected
                              ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)]'
                              : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)] border border-transparent'
                          } ${!rid ? 'opacity-40 cursor-not-allowed' : ''} ${isHeld ? 'cursor-default' : ''}`}
                        >
                          <div
                            className={`mt-0.5 size-3.5 rounded border-2 shrink-0 flex items-center justify-center ${
                              isSelected
                                ? 'border-[var(--slate)] bg-[var(--slate)]'
                                : 'border-[var(--rule)]'
                            }`}
                          >
                            {isSelected && <Check className="size-2 text-white" strokeWidth={3} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                                {role.displayName}
                              </p>
                              {isHeld && (
                                <span className="rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-sec)]">
                                  Already held
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] truncate">
                              {role.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {newlySelectedIds.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
            Where do these roles apply?
          </label>
          <select
            value={orgUnitId}
            onChange={(e) => setOrgUnitId(e.target.value)}
            className="bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] w-full border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="*">Workspace-wide</option>
            {orgUnits.map((o) => (
              <option key={o.id} value={o.id}>
                {'  '.repeat(o.depth ?? 0)}{o.displayName}{o.type ? ` (${o.type})` : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-1">
            Assigned as supporting roles. Use a package for primary-tier assignment.
          </p>
        </div>
      )}

      {error && (
        <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] whitespace-pre-wrap">{error}</p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          disabled={newlySelectedIds.length === 0 || saving}
          onClick={() => void handleAssign()}
          className="bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving && <Loader2 className="size-3.5 animate-spin" />}
          Assign {newlySelectedIds.length > 0 ? `${newlySelectedIds.length} role${newlySelectedIds.length !== 1 ? 's' : ''}` : 'roles'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Root modal
// ============================================================================

export function AssignRoleModal({
  user,
  workspaceId,
  currentUserId,
  heldRoleIds = [],
  isWorkspaceAdmin = false,
  orgUnits,
  onSuccess,
  onClose,
}: AssignRoleModalProps) {
  const [mode, setMode] = useState<AssignMode>('package');

  function handleSuccess() {
    onSuccess();
  }

  const activeTab = TABS.find((t) => t.id === mode)!;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] p-6 mx-4 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Assign roles to {user.name}
            </h2>
            <p className="mt-0.5 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--surface-raised)]"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setMode(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${
                mode === tab.id
                  ? activeTab.activeClass
                  : 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)]'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {mode === 'package' && (
          <PackageTab
            user={user}
            workspaceId={workspaceId}
            currentUserId={currentUserId}
            onSuccess={handleSuccess}
          />
        )}
        {mode === 'access' && (
          <AccessTab
            user={user}
            workspaceId={workspaceId}
            isWorkspaceAdmin={isWorkspaceAdmin}
            onSuccess={handleSuccess}
          />
        )}
        {mode === 'role' && (
          <RoleTab
            user={user}
            workspaceId={workspaceId}
            orgUnits={orgUnits}
            heldRoleIds={heldRoleIds}
            onSuccess={handleSuccess}
          />
        )}

        <div className="flex justify-start border-t border-[var(--rule)] dark:border-[var(--rule)] pt-3">
          <button
            onClick={onClose}
            className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
